import { supabase } from '../lib/supabase';
import { calculatePersonalityXP } from './lumoPersonalityService';
import { updateLeaderboardStats } from './leaderboardService';

/**
 * Service for managing XP rewards, points, and progression
 * Implements coaching platform reward system with score-based thresholds and personality-based bonuses
 */

// Thresholds and reward structure (tuned for more challenge)
const REWARD_CONFIG = {
  // Minimum score thresholds for earning rewards
  thresholds: {
    bonus: 88,        // Must score 88+ to earn bonus XP
    standard: 72      // Must score 72+ to earn standard XP
  },

  // XP rewards by activity type and performance
  xpRewards: {
    conversationAnalysis: {
      aboveBonus: 0,   // Analyzer never grants XP directly
      aboveStandard: 0,
      below: 0
    },
    roleplay: {
      aboveBonus: 40,
      aboveStandard: 22,
      below: 0
    },
    dailyDrill: {
      aboveBonus: 28,
      aboveStandard: 16,
      below: 0
    },
    trainingCompletion: {
      aboveBonus: 50,  // Bonus for high score
      standard: 30     // Standard completion
    }
  },

  // Bonus point rewards for training
  bonusTraining: {
    'discovery-mastery': 50,
    'objection-mastery': 50,
    'closing-mastery': 50,
    'listening-skills': 40,
    'confidence-selling': 45,
    'follow-up-strategy': 40
  },

  // Adaptive learning bonuses (reduced by 70%)
  adaptiveBonuses: {
    difficultyMastery: 12,
    perfectStreak: 6,
    improvementBonus: 4,
    consistencyReward: 3
  }
};
/**
 * Calculate XP earned based on overall score with personality bonuses.
 * @param {number} overallScore - Score 0-100
 * @param {string} activityType - Activity type
 * @param {string} skillLevel - User skill level
 * @param {Object} achievement - Achievement data
 * @returns {{ xp: number, eligible: boolean, threshold: number, message: string }} XP reward details
 */
export const calculateXpReward = (overallScore, activityType = 'conversationAnalysis', skillLevel = 'beginner', achievement = {}) => {
  const bonusThreshold = REWARD_CONFIG.thresholds.bonus;
  const standardThreshold = REWARD_CONFIG.thresholds.standard;

  // Tougher base XP: 20% of score (85% score ≈ 17 base XP)
  let baseXp = Math.round(overallScore * 0.2);
  
  // Add multipliers for exceptional performance
  if (overallScore >= 92) {
    baseXp = Math.round(baseXp * 1.4); // 40% bonus for 92%+
  } else if (overallScore >= bonusThreshold) {
    baseXp = Math.round(baseXp * 1.2); // 20% bonus for 88-91%
  }
  
  // Apply difficulty scaling penalty/bonus
  if (achievement.contentDifficulty && achievement.userSkillLevel) {
    const difficultyGap = achievement.contentDifficulty - achievement.userSkillLevel;
    if (difficultyGap < 0) {
      // Content is BELOW user's level - stronger XP penalty
      const penalty = Math.abs(difficultyGap) * 0.5; // 50% penalty per level below
      baseXp = Math.round(baseXp * Math.max(0.05, 1 - penalty)); // Minimum 5% XP
    } else if (difficultyGap > 0) {
      // Content is ABOVE user's level - XP bonus for challenge
      const bonus = difficultyGap * 0.25; // 25% bonus per level above
      baseXp = Math.round(baseXp * (1 + bonus));
    }
  }
  
  let eligible = overallScore >= standardThreshold;
  let motivationalMessage = '';

  if (overallScore >= 92) {
    motivationalMessage = `Outstanding! ${overallScore}% - You're mastering this! Earned ${baseXp} XP`;
  } else if (overallScore >= bonusThreshold) {
    motivationalMessage = `Excellent work! ${overallScore}% - Keep up the great performance! Earned ${baseXp} XP`;
  } else if (overallScore >= standardThreshold) {
    motivationalMessage = `Good effort! ${overallScore}% - You're improving! Earned ${baseXp} XP`;
  } else {
    motivationalMessage = `Keep practicing! ${overallScore}% - Progress comes with challenge.`;
  }

  // Apply personality-based XP multipliers
  const achievementData = {
    ...achievement,
    score: overallScore,
    scoreImprovement: achievement.scoreImprovement || 0,
    dailyStreak: achievement.dailyStreak || 0
  };
  
  let finalXp = calculatePersonalityXP(baseXp, skillLevel, achievementData);

  // Apply adaptive learning bonuses
  const bonuses = [];
  
  if (achievement.difficultyLevelUp) {
    finalXp += REWARD_CONFIG.adaptiveBonuses.difficultyMastery;
    bonuses.push(`+${REWARD_CONFIG.adaptiveBonuses.difficultyMastery} XP - Difficulty Mastery!`);
  }
  
  if (achievement.perfectStreak >= 5) {
    finalXp += REWARD_CONFIG.adaptiveBonuses.perfectStreak;
    bonuses.push(`+${REWARD_CONFIG.adaptiveBonuses.perfectStreak} XP - Perfect Streak!`);
  }
  
  if (achievement.scoreImprovement >= 20) {
    finalXp += REWARD_CONFIG.adaptiveBonuses.improvementBonus;
    bonuses.push(`+${REWARD_CONFIG.adaptiveBonuses.improvementBonus} XP - Major Improvement!`);
  }
  
  if (achievement.dailyStreak >= 7) {
    finalXp += REWARD_CONFIG.adaptiveBonuses.consistencyReward;
    bonuses.push(`+${REWARD_CONFIG.adaptiveBonuses.consistencyReward} XP - Week Streak!`);
  }

  return {
    xp: finalXp,
    baseXp,
    eligible,
    bonusThreshold,
    activityScore: overallScore,
    message: motivationalMessage,
    personalityBonus: finalXp - baseXp,
    bonuses
  };
};

// Get training course reward info
export const getTrainingReward = (courseId) => {
  const bonusConfig = REWARD_CONFIG.bonusTraining || {};
  const points = bonusConfig[courseId] || 40;
  return {
    points,
    message: `Complete this training to earn ${points} bonus XP!`
  };
};

/**
 * Award XP to user and update profile
 * @param {string} userId - User ID
 * @param {number} xpAmount - Amount of XP to award
 * @param {string} reason - Reason for XP (e.g., 'conversation_analysis', 'training_completion')
 * @returns {Promise<Object>} - Updated user profile with new XP total
 */
// XP configuration: non-linear curve with increasing XP per level brackets
const LEVEL_BRACKETS = [
  { upTo: 10, xpPerLevel: 1200 },
  { upTo: 20, xpPerLevel: 1500 },
  { upTo: 30, xpPerLevel: 1800 },
  { upTo: 50, xpPerLevel: 2200 },
  { upTo: 60, xpPerLevel: 2600 },
  { upTo: 75, xpPerLevel: 3000 },
  { upTo: 100, xpPerLevel: 3500 }
];

const xpPerLevelFor = (level) => {
  // level 1 requires 0; from level 1 -> 2 uses the first bracket
  for (const bracket of LEVEL_BRACKETS) {
    if (level <= bracket.upTo) return bracket.xpPerLevel;
  }
  return 4000;
};

const levelFromXp = (xp) => {
  if (!xp || xp <= 0) return 1;
  let level = 1;
  let remaining = xp;
  while (true) {
    const need = xpPerLevelFor(level);
    if (remaining >= need) {
      remaining -= need;
      level += 1;
      if (level >= 100) break;
    } else {
      break;
    }
  }
  return level;
};

// XP floor (minimum XP) required to be at a given level
const xpFloorForLevel = (level) => {
  if (!level || level <= 1) return 0;
  let floor = 0;
  for (let l = 1; l < level; l++) {
    floor += xpPerLevelFor(l);
  }
  return floor;
};

// Daily XP caps
const DAILY_XP_SOFT_CAP = 200;
const DAILY_XP_HARD_CAP = 350;
const ROLEPLAY_DAILY_CAP = 250;
const DAILY_DRILL_DAILY_CAP = 120;

export const awardXp = async (userId, xpAmount, reason = 'activity') => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (xpAmount < 0) throw new Error('XP amount cannot be negative');

    // Fetch current user profile
    const { data: profile, error: fetchError } = await supabase
      .from('user_profiles')
      .select('xp_points, level, lifetime_xp')
      .eq('id', userId)
      .single();

    if (fetchError) throw fetchError;

    const currentXp = profile?.xp_points || 0;
    const currentLifetimeXp = profile?.lifetime_xp || 0;
    // Apply daily XP caps using xp_transactions (graceful fallback)
    let adjustedXpAmount = xpAmount;
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data: txs } = await supabase
        .from('xp_transactions')
        .select('xp_amount, created_at')
        .gte('created_at', `${today}T00:00:00.000Z`)
        .lte('created_at', `${today}T23:59:59.999Z`);
      const todayTotal = (txs || []).reduce((sum, t) => sum + (t?.xp_amount || 0), 0);
      // Per-activity caps
      if (reason) {
        const { data: actTxs } = await supabase
          .from('xp_transactions')
          .select('xp_amount, reason, created_at')
          .eq('reason', reason)
          .gte('created_at', `${today}T00:00:00.000Z`)
          .lte('created_at', `${today}T23:59:59.999Z`);
        const actTotal = (actTxs || []).reduce((sum, t) => sum + (t?.xp_amount || 0), 0);
        let actCap = null;
        if (String(reason).includes('daily_drill')) actCap = DAILY_DRILL_DAILY_CAP;
        else if (String(reason).includes('technique') || String(reason).includes('roleplay')) actCap = ROLEPLAY_DAILY_CAP;
        if (actCap !== null) {
          const remainingAct = Math.max(0, actCap - actTotal);
          adjustedXpAmount = Math.min(adjustedXpAmount, remainingAct);
        }
      }
      const projected = todayTotal + xpAmount;
      if (projected > DAILY_XP_SOFT_CAP && projected <= DAILY_XP_HARD_CAP) {
        adjustedXpAmount = Math.round(xpAmount * 0.5); // 50% after soft cap
      } else if (projected > DAILY_XP_HARD_CAP) {
        const remaining = Math.max(0, DAILY_XP_HARD_CAP - todayTotal);
        adjustedXpAmount = Math.min(adjustedXpAmount, remaining);
      }
    } catch (capErr) {
      // If xp_transactions is unavailable, skip caps silently
      console.warn('Daily XP cap check skipped:', capErr?.message || capErr);
    }

    // (moved applyInactivityDecayIfNeeded and getInactivityWarning to top-level)
    const newXp = currentXp + adjustedXpAmount;
    const newLifetimeXp = currentLifetimeXp + xpAmount;
    
    // Calculate new level via non-linear curve
    const newLevel = levelFromXp(newXp);

    // Update user profile with new XP, lifetime XP, and level
    const { data: updatedProfile, error: updateError } = await supabase
      .from('user_profiles')
      .update({
        xp_points: newXp,
        lifetime_xp: newLifetimeXp,
        level: newLevel,
        last_practice_date: new Date().toISOString().split('T')[0]
      })
      .eq('id', userId)
      .select()
      .single();

    if (updateError) throw updateError;

    // Update real-time daily stats for Lumo chat mini dashboard
    dailyStatsService.addXp(adjustedXpAmount);

    // Log XP transaction for analytics (optional table)
    // Optional: Log XP transaction for analytics (skip if table doesn't exist)
    try {
      await supabase.from('xp_transactions').insert([{
        user_id: userId,
        xp_amount: adjustedXpAmount,
        reason: reason,
        new_total: newXp,
        level_before: profile?.level,
        level_after: newLevel,
        created_at: new Date().toISOString()
      }]);
    } catch (err) {
      console.warn('XP transaction logging failed (table may not exist):', err);
      // Don't throw - this is optional analytics
    }

    return {
      success: true,
      newXp,
      xpAwarded: xpAmount,
      newLevel,
      leveledUp: newLevel > (profile?.level || 1),
      levelUpMessage: newLevel > (profile?.level || 1) 
        ? `🚀 Level Up! You're now level ${newLevel}!` 
        : null
    };
  } catch (error) {
    console.error('Error awarding XP:', error);
    throw error;
  }
};

/**
 * Apply weekly inactivity decay if user hasn't practiced.
 * - 2% of current XP per full inactive week
 * - Updates `xp_points`, recalculates level via non-linear curve, logs transaction
 * - Sets `last_decay_date` to today to prevent double application
 */
export const applyInactivityDecayIfNeeded = async (userId) => {
  try {
    if (!userId) throw new Error('User ID is required');
    const { data: profile, error } = await supabase
      .from('user_profiles')
      .select('xp_points, level, last_practice_date, last_decay_date')
      .eq('id', userId)
      .single();
    if (error) throw error;

    const lastPractice = profile?.last_practice_date ? new Date(profile.last_practice_date) : null;
    if (!lastPractice) return { applied: false };
    const today = new Date();

    // Determine baseline date for decay
    const baseline = profile?.last_decay_date ? new Date(profile.last_decay_date) : lastPractice;
    const msPerDay = 1000 * 60 * 60 * 24;
    const days = Math.floor((today - baseline) / msPerDay);
    const weeks = Math.floor(days / 7);
    if (weeks <= 0) return { applied: false };

    // Apply compound 2% per week
    const currentXp = profile?.xp_points || 0;
    let decayedXp = currentXp;
    for (let i = 0; i < weeks; i++) {
      decayedXp = Math.round(decayedXp * 0.98);
    }
    const totalDecay = Math.max(0, currentXp - decayedXp);
    if (totalDecay <= 0) return { applied: false };

    // Safeguard: Do not drop below current level's floor
    const floor = xpFloorForLevel(profile?.level || 1);
    decayedXp = Math.max(decayedXp, floor);
    const newLevel = levelFromXp(decayedXp);

    const { error: updErr } = await supabase
      .from('user_profiles')
      .update({ xp_points: decayedXp, level: newLevel, last_decay_date: new Date().toISOString().split('T')[0] })
      .eq('id', userId);
    if (updErr) throw updErr;

    // Log decay transaction (optional)
    try {
      await supabase.from('xp_transactions').insert([{
        user_id: userId,
        xp_amount: -totalDecay,
        reason: 'inactivity_decay',
        new_total: decayedXp,
        level_before: profile?.level,
        level_after: newLevel,
        created_at: new Date().toISOString()
      }]);
    } catch (txErr) {
      console.warn('Decay transaction logging failed:', txErr?.message || txErr);
    }

    return { applied: true, totalDecay, newXp: decayedXp, newLevel };
  } catch (err) {
    console.error('Error applying inactivity decay:', err);
    return { applied: false, error: err?.message || String(err) };
  }
};

/**
 * Get an inactivity warning if decay is approaching.
 * Returns next decay date and XP at risk (2% of current XP).
 */
export const getInactivityWarning = async (userId) => {
  try {
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('xp_points, last_practice_date, last_decay_date')
      .eq('id', userId)
      .single();
    const xp = profile?.xp_points || 0;
    const lastPractice = profile?.last_practice_date ? new Date(profile.last_practice_date) : null;
    if (!lastPractice) return null;

    const baseline = profile?.last_decay_date ? new Date(profile.last_decay_date) : lastPractice;
    const msPerDay = 1000 * 60 * 60 * 24;
    const today = new Date();
    const daysSincePractice = Math.floor((today - lastPractice) / msPerDay);

    // Next decay: baseline advanced to the next 7-day boundary
    const daysSinceBaseline = Math.floor((today - baseline) / msPerDay);
    const weeksSinceBaseline = Math.floor(daysSinceBaseline / 7);
    const nextBoundaryDays = (weeksSinceBaseline + 1) * 7;
    const nextDecayDate = new Date(baseline.getTime() + nextBoundaryDays * msPerDay);
    const xpAtRisk = Math.round(xp * 0.02);

    // Show warning if within 2 days of next decay or 6+ days inactive
    const daysUntilDecay = Math.max(0, Math.ceil((nextDecayDate - today) / msPerDay));
    const show = daysSincePractice >= 6 || daysUntilDecay <= 2;
    if (!show) return null;

    return {
      nextDecayDate: nextDecayDate.toISOString().split('T')[0],
      xpAtRisk,
      daysUntilDecay,
      daysSincePractice
    };
  } catch (err) {
    console.error('Error computing inactivity warning:', err);
    return null;
  }
};

/**
 * Award XP and update leaderboard stats (unified function)
 * @param {string} userId - User ID
 * @param {number} xpAmount - XP to award
 * @param {string} activityType - 'drill', 'objection', 'training'
 * @param {Object} activityData - Additional activity data (score, difficulty, etc.)
 */
export const awardXpAndUpdateLeaderboard = async (userId, xpAmount, activityType, activityData = {}) => {
  try {
    // Award XP to user profile
    const xpResult = await awardXpToUser(userId, xpAmount, activityType);

    // Update leaderboard stats
    await updateLeaderboardStats(
      userId,
      activityType,
      xpAmount,
      activityData.score || 0,
      activityData.difficulty || 1,
      activityData.isPerfect || false
    );

    return xpResult;
  } catch (error) {
    console.error('Error awarding XP and updating leaderboard:', error);
    throw error;
  }
};

/**
 * Check if user is eligible for bonus based on score
 * @param {number} score - Score (0-100)
 * @returns {boolean}
 */
export const isBonusEligible = (score) => {
  return score >= REWARD_CONFIG.thresholds.bonus;
};

/**
 * Get all training courses with their XP rewards
 * @returns {Object} - Training courses with metadata
 */
export const getTrainingCourses = () => {
  return {
    'discovery-mastery': {
      id: 'discovery-mastery',
      name: 'Discovery Mastery',
      description: 'Master the art of discovery questions to uncover deeper prospect needs',
      points: 50,
      duration: '15 min',
      focus: 'discovery_gaps',
      tags: ['discovery', 'questioning', 'needs-analysis'],
      tier: 'core',
      unlockLevel: 1
    },
    'objection-mastery': {
      id: 'objection-mastery',
      name: 'Objection Mastery',
      description: 'Learn proven techniques to handle common objections with confidence',
      points: 50,
      duration: '12 min',
      focus: 'objection_gaps',
      tags: ['objections', 'handling', 'confidence'],
      tier: 'core',
      unlockLevel: 1
    },
    'closing-mastery': {
      id: 'closing-mastery',
      name: 'Closing Mastery',
      description: 'Master trial closes and commitment strategies to seal the deal',
      points: 50,
      duration: '10 min',
      focus: 'closing_gaps',
      tags: ['closing', 'trial-close', 'commitment'],
      tier: 'core',
      unlockLevel: 1
    },
    'listening-skills': {
      id: 'listening-skills',
      name: 'Active Listening Skills',
      description: 'Develop deeper listening to understand prospect concerns better',
      points: 40,
      duration: '8 min',
      focus: 'listening_gaps',
      tags: ['listening', 'communication', 'rapport'],
      tier: 'advanced',
      unlockLevel: 3
    },
    'confidence-selling': {
      id: 'confidence-selling',
      name: 'Confident Selling',
      description: 'Build genuine confidence in your value proposition and delivery',
      points: 45,
      duration: '10 min',
      focus: 'confidence_gaps',
      tags: ['confidence', 'authority', 'positioning'],
      tier: 'advanced',
      unlockLevel: 4
    },
    'follow-up-strategy': {
      id: 'follow-up-strategy',
      name: 'Follow-Up Mastery',
      description: 'Create structured follow-up sequences that move deals forward',
      points: 40,
      duration: '9 min',
      focus: 'follow_up_gaps',
      tags: ['follow-up', 'persistence', 'sequencing'],
      tier: 'advanced',
      unlockLevel: 5
    }
  };
};

export default {
  calculateXpReward,
  getTrainingReward,
  awardXp,
  isBonusEligible,
  getTrainingCourses,
  REWARD_CONFIG
};
